package com.nonopn.skydome.common.app.pages.main.view.map

import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.ExperimentalMaterialApi
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Search
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.nonopn.icons.FixedGps
import com.nonopn.icons.ZoomIn
import com.nonopn.icons.ZoomOut
import com.nonopn.skydome.common.app.api.GeocodeResult
import com.nonopn.skydome.common.app.home.HotPreviewApp
import com.nonopn.skydome.common.app.pages.main.model.FakeMainScreenModel
import com.nonopn.skydome.common.app.pages.main.model.IMapAction
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.map_enter_address
import com.nonopn.skywatcher.design.theme.LocalApplicationColorTheme
import org.jetbrains.compose.resources.stringResource
import com.nonopn.skywatcher.design.utils.LocalFrame
import com.nonopn.skywatcher.design.utils.WindowType
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.design.widgets.defaultCardBackground
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.CustomOutlinedEditText
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.platform.Platform
import eu.codlab.platform.currentPlatform

@Composable
fun MapButtons(
    modifier: Modifier,
    model: IMapAction
) {
    var showSearch by remember { mutableStateOf(false) }
    val state by model.lookUpState.collectAsState()

    Column(
        modifier = modifier,
    ) {
        if (LocalFrame.current == WindowType.TABLET || LocalFrame.current == WindowType.PHABLET) {
            Row(
                Modifier.height(50.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ShowActions(model) {
                    showSearch = !showSearch
                }
            }
        } else {
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                ShowActions(model) {
                    showSearch = !showSearch
                }
            }
        }

        if (showSearch) {
            SearchPanel(
                query = "",
                suggestions = state.results,
                onQueryChange = { model.lookUp(it) },
            ) {
                showSearch = false
                model.moveTo(it.location)
            }
        }
    }
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun SearchPanel(
    query: String,
    suggestions: List<GeocodeResult>,
    onQueryChange: (String) -> Unit,
    onSuggestionClick: (GeocodeResult) -> Unit
) {
    val focusRequester = remember { FocusRequester() }
    var actualQuery by remember { mutableStateOf(TextFieldValue(query)) }

    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    MapOverlayCard(
        modifier = Modifier
            .widthIn(max = 300.dp)
            .height(IntrinsicSize.Min)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            CustomOutlinedEditText(
                modifier = Modifier.fillMaxWidth()
                    .focusRequester(focusRequester),
                value = actualQuery,
                onValueChanged = {
                    actualQuery = it
                    onQueryChange(it.text)
                },
                label = { TextNormal(stringResource(Res.string.map_enter_address)) },
            )

            Spacer(modifier = Modifier.height(8.dp))

            suggestions.forEach { suggestion ->
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSuggestionClick(suggestion) }
                        .padding(8.dp)
                ) {
                    TextNormal(suggestion.formattedAddress)
                }
            }
        }
    }
}

val MapButtonsRoundedCorner = RoundedCornerShape(50)

@Composable
fun IconButton(
    modifier: Modifier = Modifier,
    imageVector: ImageVector,
    contentDescription: String,
    onClick: () -> Unit,
) {
    val colors = LocalApplicationColorTheme.current
    val color = defaultCardBackground()

    Column(modifier.clip(MapButtonsRoundedCorner)) {
        DefaultCard(
            onClick = onClick,
            backgroundColor = color,
            shape = MapButtonsRoundedCorner,
            columnModifier = Modifier.padding(8.dp)
        ) {
            Icon(
                imageVector = imageVector,
                contentDescription = contentDescription,
                tint = colors.fonts.regular
            )
        }
    }
}

@Composable
private fun ShowActions(
    model: IMapAction,
    onSearch: (() -> Unit)? = null
) {
    val mobile = currentPlatform == Platform.IOS || currentPlatform == Platform.ANDROID

    onSearch?.let {
        IconButton(
            imageVector = Icons.Rounded.Search,
            contentDescription = "Search",
            onClick = it
        )
    }

    if (!mobile) {
        IconButton(
            imageVector = Icons.Rounded.ZoomIn,
            contentDescription = "Zoom In",
            onClick = { model.zoomIn() }
        )

        IconButton(
            imageVector = Icons.Rounded.ZoomOut,
            contentDescription = "Zoom Out",
            onClick = { model.zoomOut() }
        )
    } else {
        IconButton(
            imageVector = Icons.Rounded.FixedGps,
            contentDescription = "GPS",
            onClick = { model.moveToOrigin() }
        )
    }
}

@Composable
@HotPreview(widthDp = 250, heightDp = 50, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 50, darkMode = false)
private fun MapButtonsPreview() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme()) {
        MapButtons(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(false)
        )
    }
}
